<template>
  <div class="ai-assistant" :class="{ 'ai-entry': detail, 'ai-form-entry': !detail }">
    <template v-if="detail">
      <div class="ai-entry-copy"><h2><app-icon name="spark" />AI 销售参谋</h2><p>{{ result ? result.analysis.summary.text : '结合客户资料与跟进记录，看看下一步怎么推进' }}</p></div>
    </template>
    <p class="ai-entry-status" aria-live="polite">{{ entryStatus }}</p>
    <div class="ai-entry-actions">
      <button ref="historyTrigger" type="button" class="button small outline" :disabled="disabled || busy || historyBusy || saveBusy || pendingSave" @click="openHistory">历史分析</button>
      <button ref="trigger" type="button" class="button small soft" :disabled="disabled || busy || historyBusy || saveBusy" @click="startAnalysis">
        <span v-if="busy" class="spinner" /><app-icon v-else name="spark" />
        {{ busy ? '正在分析' : generateLabel }}
      </button>
    </div>
    <p v-if="error && !open" class="error-message ai-entry-error" role="alert">{{ error }}</p>

    <transition name="sheet" @after-leave="syncLock">
      <div v-if="open" class="sheet-layer ai-layer" @keydown.esc="close">
        <div class="scrim" @click="close" @touchmove.prevent />
        <section ref="dialog" class="sheet ai-sheet" role="dialog" aria-modal="true" :aria-label="historyView ? '最近一次分析' : 'AI 客户分析'" tabindex="-1" @keydown.tab="trapFocus">
          <div class="sheet-grab" />
          <header class="sheet-head">
            <div><h2>{{ historyView ? '最近一次分析' : 'AI 客户分析' }}</h2></div>
            <button type="button" class="sheet-close" @click="close">收起</button>
          </header>
          <div v-if="busy" class="ai-progress" role="status"><span class="spinner" /><div><b>正在结合资料与跟进记录分析</b><p>分析完成后会给出跟进目标、时间和话术</p></div></div>
          <div v-if="historyBusy" class="ai-progress" role="status"><span class="spinner" /><b>正在读取最近一次分析</b></div>
          <p v-if="error" class="error-message" role="alert">{{ error }}</p>
          <p v-if="historyView && !historyBusy && !error && !result" class="ai-no-result">还没有历史分析，点击 AI 分析可生成第一份结果。</p>
          <template v-if="showResult">
            <div class="ai-records-bar"><p class="ai-meta">{{ timeText(result.generated_at) }} · {{ result.saved === false ? '待保存' : '已存档' }}<span v-if="result.meta.has_draft"> · 含未保存内容</span></p></div>
            <div v-if="result.meta.persistence_pending" class="notice" role="alert">分析已生成，但尚未存档。请保留当前页面并重试保存，无需重新分析。<button type="button" class="text-button" :disabled="saveBusy" @click="retrySave">{{ saveBusy ? '正在保存' : '重试保存分析' }}</button></div>
            <p v-if="saveError" class="error-message" role="alert">{{ saveError }}</p>
            <div v-if="stale" class="notice ai-stale" role="status">资料或沟通内容已变化，或建议时间已过。这份分析供回看，请重新分析后采用。</div>
            <p v-if="result.meta.history_limited" class="ai-caption">本次结合近期跟进、重点记录与首次需求，部分历史长文未纳入。</p>
            <customer-ai-result ref="analysisResult" :key="result.id" :result="result" :profile-values="profileValues" :profile-choices="profileChoices" :detail="detail" :stale="stale" :disabled="disabled || busy" @schedule="schedule" @profile="applyProfile" @profile-selection="profileSelection = $event" />

            <details v-if="!detail && result.content" class="ai-draft">
              <summary>本次跟进建议稿</summary>
              <p class="ai-caption">只润色本次沟通，请核对后采用</p>
              <textarea v-model="draft" maxlength="6000" aria-label="本次跟进建议稿" />
              <label v-if="allowKeywords && result.keyword" class="check-line"><input v-model="useKeyword" type="checkbox" />同时填入关键词：{{ result.keyword }}</label>
              <button class="button small soft" type="button" :disabled="disabled || busy || stale || !draft.trim()" @click="applyDraft">采用建议稿，继续编辑</button>
            </details>

            <section v-if="!result.meta.persistence_pending" class="ai-feedback">
              <p>{{ feedbackDone ? '反馈已记录，感谢帮助改进建议' : '这次分析对跟进有帮助吗？' }}</p>
              <div class="ai-feedback-actions">
                <button class="text-button" type="button" :disabled="feedbackBusy || feedbackDone" @click="sendFeedback('helpful')">有帮助</button>
                <button class="text-button" type="button" :disabled="feedbackBusy || feedbackDone" @click="feedbackOpen = !feedbackOpen">判断不准</button>
              </div>
              <div v-if="feedbackOpen && !feedbackDone" class="ai-feedback-form">
                <textarea v-model="feedbackReason" maxlength="250" placeholder="哪里判断不准？可选填，最多250字" aria-label="分析反馈说明" />
                <button class="button small soft" type="button" :disabled="feedbackBusy" @click="sendFeedback('inaccurate')">{{ feedbackBusy ? '正在提交' : '提交反馈' }}</button>
              </div>
              <p v-if="feedbackError" class="error-message" role="alert">{{ feedbackError }}</p>
            </section>
          </template>
          <p v-if="generationFailed" class="ai-no-result">本次分析未完成，填写内容和历史记录均已保留。</p>
          <div class="sheet-foot" :class="{ two: !busy && !historyBusy }">
            <template v-if="profileMode">
              <button class="button outline" type="button" :disabled="disabled || (stale && pendingSave)" @click="stale ? startAnalysis() : close()">{{ stale ? '重新分析' : '暂不采用' }}</button>
              <button class="button" type="button" :disabled="disabled || stale || pendingSave || !profileSelection.count" @click="applySelectedProfile">{{ detail ? '到表单核对' : '填入画像' }}{{ profileSelection.count ? '（' + profileSelection.count + '）' : '' }}</button>
            </template>
            <template v-else>
            <button v-if="!busy && !historyBusy && historyView && error" class="button outline" type="button" :disabled="disabled" @click="openHistory">重试读取</button>
            <button v-else-if="!busy && !historyBusy && (showResult || historyView)" class="button outline" type="button" :disabled="disabled || saveBusy || pendingSave" @click="startAnalysis">{{ hasRecords ? '重新分析' : 'AI 分析' }}</button>
            <button v-else-if="!busy && !historyBusy" class="button outline" type="button" :disabled="disabled" @click="openHistory">历史分析</button>
            <button class="button" type="button" @click="busy || historyBusy || showResult || historyView ? close() : startAnalysis()">{{ busy || historyBusy ? '收起，稍后查看' : showResult || historyView ? (detail ? '返回客户详情' : '继续记录跟进') : '重试分析' }}</button>
            </template>
          </div>
        </section>
      </div>
    </transition>
  </div>
</template>
<script>
import CustomerAiResult from './customer-ai-result'
import { aiProfile, validAiTime, syncAiModalLock } from '../common/ai'
import { dateTimeText } from '../common/when'
import { sameProfileValue } from '../common/ai-profile'
export default {
  components: { CustomerAiResult },
  props: {
    customerId: { type: [Number, String], default: null },
    draftKey: { type: String, default: '' },
    content: { type: String, default: '' },
    profile: { type: Object, default: () => ({}) },
    profileValues: { type: Object, default: () => ({}) },
    profileChoices: { type: Object, default: () => ({}) },
    disabled: Boolean, allowKeywords: Boolean, detail: Boolean
  },
  data() {
    return {
      open: false, busy: false, error: '', result: null, requestId: 0, generationFailed: false, lastTrigger: 'generate',
      originalContent: '', originalProfile: '', adoptedContent: null, adoptedProfile: null,
      draft: '', useKeyword: false, feedbackOpen: false, feedbackReason: '',
      feedbackDone: false, feedbackBusy: false, feedbackError: '',
      historyView: false, historyBusy: false,
      saveBusy: false, saveError: '',
      profileSelection: { active: false, count: 0 }
    }
  },
  computed: {
    showResult() { return !!this.result && !this.busy && !this.historyBusy && !this.error && !this.generationFailed },
    profileMode() { return this.showResult && this.profileSelection.active },
    hasRecords() { return !!(this.result && this.result.saved !== false) },
    pendingSave() { return !!(this.result && this.result.meta.persistence_pending) },
    generateLabel() { return this.pendingSave ? '保存分析' : this.hasRecords ? '重新分析' : 'AI 分析' },
    entryStatus() {
      if (this.busy) return '正在分析当前资料和本次跟进'
      if (this.generationFailed) return '本次分析未完成，可重试或查看历史'
      if (this.pendingSave) return '分析尚未存档，请先保存结果'
      if (this.stale) return '资料或跟进有更新，可重新分析获取新建议'
      if (this.result) return '已存档 · ' + this.timeText(this.result.generated_at)
      return '结合当前资料和跟进，提供销售建议'
    },
    profileKey() { return JSON.stringify(aiProfile(this.profile)) },
    stale() {
      return !!this.result && (this.result.stale || (this.profileKey !== this.originalProfile && this.profileKey !== this.adoptedProfile) ||
        (this.content !== this.originalContent && this.content !== this.adoptedContent) ||
        (!!this.result.analysis.follow_up.timing.scheduled_at && !validAiTime(this.result.analysis.follow_up.timing.scheduled_at)))
    }
  },
  watch: {
    customerId() { this.reset(); this.loadLatest() },
    open(value) {
      this.$nextTick(() => {
        syncAiModalLock()
        if (value && this.$refs.dialog) this.$refs.dialog.focus()
        else {
          const trigger = this.lastTrigger === 'history' ? this.$refs.historyTrigger : this.$refs.trigger
          if (trigger) trigger.focus()
        }
      })
    }
  },
  mounted() { this.loadLatest() },
  beforeDestroy() {
    this.requestId++
    this.$nextTick(syncAiModalLock)
  },
  methods: {
    timeText(value) { return dateTimeText(value) },
    reset() {
      this.requestId++; this.result = null; this.error = ''; this.open = false; this.busy = false
      this.historyView = false; this.historyBusy = false; this.generationFailed = false
    },
    payload() { return { customer_id: this.customerId || undefined, draft_key: this.draftKey || undefined, content: this.content, profile: aiProfile(this.profile) } },
    receive(result, content, profileKey) {
      this.result = result
      this.profileSelection = { active: false, count: 0 }
      this.generationFailed = false
      this.error = ''
      this.originalContent = content
      this.originalProfile = profileKey
      this.adoptedContent = null
      this.adoptedProfile = null
      this.draft = result.content || ''
      this.useKeyword = false
      this.feedbackDone = !!result.feedback_rating
      this.feedbackOpen = false
      this.feedbackReason = ''
      this.feedbackError = ''
      this.saveError = ''
      this.$nextTick(this.scrollTop)
    },
    async loadLatest() {
      if (!this.customerId && !this.draftKey) return
      const requestId = ++this.requestId
      const content = this.content
      const profileKey = this.profileKey
      try {
        const data = await this.$api.post('/api/ai/customer-analysis/latest', this.payload())
        if (!this._isDestroyed && requestId === this.requestId && data.result && data.result.analysis) this.receive(data.result, content, profileKey)
      } catch (e) { /* Opening a customer never depends on an AI snapshot. */ }
    },
    startAnalysis() {
      if (this.disabled || this.busy || this.historyBusy || this.saveBusy) return
      this.lastTrigger = 'generate'
      this.open = true
      this.historyView = false
      this.generationFailed = false
      this.error = ''
      if (!this.pendingSave) this.analyze(true)
    },
    close() { this.open = false },
    syncLock() { syncAiModalLock() },
    scrollTop() { if (this.$refs.dialog) this.$refs.dialog.scrollTop = 0 },
    trapFocus(event) {
      const nodes = this.$refs.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), summary')
      if (!nodes.length) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    },
    async analyze(refresh) {
      if (this.disabled || this.busy) return
      this.busy = true
      this.error = ''
      this.generationFailed = false
      this.$nextTick(this.scrollTop)
      const requestId = ++this.requestId
      const content = this.content
      const profileKey = this.profileKey
      try {
        const data = await this.$api.post('/api/ai/follow-up', Object.assign(this.payload(), { refresh }))
        if (!data.analysis || !data.analysis.follow_up) throw new Error('请更新服务端的 AI 分析接口后重试')
        if (!this._isDestroyed && requestId === this.requestId) this.receive(data, content, profileKey)
      } catch (e) {
        if (!this._isDestroyed && requestId === this.requestId) { this.error = e.message; this.generationFailed = true; this.$nextTick(this.scrollTop) }
      } finally {
        if (!this._isDestroyed && requestId === this.requestId) this.busy = false
      }
    },
    schedule(value) {
      const date = value ? validAiTime(value) : ''
      if (this.disabled || this.busy || this.stale || (value && !date)) return this.$toast('资料或时间已变化，请重新分析')
      this.$emit('schedule', date)
      this.close()
    },
    applyDraft() {
      if (this.disabled || this.busy || this.stale || !this.draft.trim()) return
      this.adoptedContent = this.draft.trim()
      this.$emit('apply', { content: this.adoptedContent, keyword: this.useKeyword ? this.result.keyword : '' })
      this.close()
    },
    applyProfile(data) {
      if (this.disabled || this.busy || this.stale || this.pendingSave) return
      this.$emit('profile', data)
      if (!this.detail) this.$nextTick(() => {
        if (data.updates.every(update => sameProfileValue(this.profileValues[update.field], update.value))) this.adoptedProfile = this.profileKey
      })
      this.close()
    },
    applySelectedProfile() { if (this.$refs.analysisResult) this.$refs.analysisResult.applyProfileSelection() },
    async sendFeedback(rating) {
      if (!this.result || this.feedbackBusy || this.feedbackDone) return
      this.feedbackBusy = true
      this.feedbackError = ''
      const analysisId = this.result.id
      try {
        await this.$api.post('/api/ai/customer-analysis/feedback', { customer_id: this.customerId || undefined, draft_key: this.draftKey || undefined, analysis_id: this.result.id, rating, reason: this.feedbackReason })
        if (!this._isDestroyed && this.result && this.result.id === analysisId) { this.feedbackDone = true; this.feedbackOpen = false }
      } catch (e) { if (!this._isDestroyed && this.result && this.result.id === analysisId) this.feedbackError = e.message }
      finally { this.feedbackBusy = false }
    },
    async openHistory() {
      if (this.disabled || this.busy || this.historyBusy || this.saveBusy || this.pendingSave) return
      const requestId = ++this.requestId
      const content = this.content
      const profileKey = this.profileKey
      this.lastTrigger = 'history'
      this.open = true
      this.error = ''
      this.generationFailed = false
      this.historyView = true
      this.historyBusy = true
      this.$nextTick(this.scrollTop)
      try {
        const data = await this.$api.post('/api/ai/customer-analysis/latest', this.payload())
        if (this._isDestroyed || requestId !== this.requestId) return
        if (data.result && data.result.analysis) this.receive(data.result, content, profileKey)
        else this.result = null
      } catch (e) { if (!this._isDestroyed && requestId === this.requestId) this.error = e.message }
      finally { if (!this._isDestroyed && requestId === this.requestId) this.historyBusy = false }
    },
    async retrySave() {
      if (this.saveBusy || !this.result) return
      this.saveBusy = true
      this.saveError = ''
      const analysisId = this.result.id
      try {
        const data = await this.$api.post('/api/ai/customer-analysis/save', Object.assign(this.payload(), { analysis_id: this.result.id }))
        if (this._isDestroyed || !this.result || this.result.id !== analysisId) return
        this.result = data
        this.$toast('分析已存档，可随时回看')
      } catch (e) { this.saveError = e.message }
      finally { this.saveBusy = false }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/customer-ai-assistant.vue